import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import MarketingNav from '../components/MarketingNav';
import { ArrowRight, Check, Palette, Wand2, BookOpen, Bot, CreditCard, BarChart3, ShieldCheck, Package, MousePointerClick, Layers, Monitor, Tablet, Smartphone, Globe, FileText, Users, Mail, Lock, Zap, Target, TrendingUp, Download, Clock } from 'lucide-react';
import { useRef, useEffect } from 'react';

function useScrollAnimate() {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (!el) return; const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; } }, { threshold: 0.1 }); obs.observe(el); return () => obs.disconnect(); }, []);
  return ref;
}
function Reveal({ children, className = '', delay = 0 }) {
  const ref = useScrollAnimate();
  return <div ref={ref} className={className} style={{ opacity: 0, transform: 'translateY(32px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>{children}</div>;
}

// Feature section component — alternates layout
function FeatureSection({ label, title, desc, bullets, mockup, dark, reversed, id }) {
  const bg = dark ? '#195259' : '#FFFFFF';
  const textMain = dark ? '#FFFFFF' : '#195259';
  const textSub = dark ? 'rgba(245,233,207,0.7)' : '#6D6E71';
  const bulletColor = dark ? 'rgba(255,154,0,0.15)' : '#195259';
  const bulletText = dark ? '#FF9A00' : '#F5E9CF';

  return (
    <section id={id} className="py-20 sm:py-28 px-4" style={{ backgroundColor: bg }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className={reversed ? 'order-1 lg:order-2' : ''}>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>{label}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight mb-6" style={{ color: textMain }}>{title}</h2>
              <p className="text-base leading-relaxed mb-8" style={{ color: textSub }}>{desc}</p>
              {bullets && (
                <div className="flex flex-wrap gap-3">
                  {bullets.map(b => (
                    <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: bulletColor, color: dark ? '#FF9A00' : '#F5E9CF' }}>
                      <Check className="h-3 w-3" /> {b}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15} className={reversed ? 'order-2 lg:order-1' : ''}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl" style={{ background: `linear-gradient(135deg, ${dark ? 'rgba(255,154,0,0.15)' : 'rgba(25,82,89,0.08)'}, transparent)`, transform: reversed ? 'rotate(-2deg)' : 'rotate(2deg)' }} />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ border: dark ? '1px solid rgba(255,255,255,0.1)' : '1.5px solid #195259' }}>
                {mockup}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function FeaturesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Lexend, sans-serif' }}>
      <style>{`@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }`}</style>

      {/* Nav */}
      <MarketingNav activePage="/features" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 sm:pt-36 sm:pb-20 px-4 overflow-hidden" style={{ backgroundColor: '#195259' }}>
        <img src="/images/marketing/element-teal-right.png" alt="" className="absolute top-0 right-0 w-[140px] sm:w-[200px] lg:w-[280px] h-auto pointer-events-none select-none" style={{ zIndex: 1, opacity: 0.5 }} />
        <img src="/images/marketing/element-teal-left.png" alt="" className="absolute bottom-0 left-0 w-[100px] sm:w-[160px] lg:w-[220px] h-auto pointer-events-none select-none" style={{ zIndex: 1, opacity: 0.5 }} />
        <div className="max-w-4xl mx-auto text-center relative" style={{ zIndex: 2 }}>
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>Features</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6">
              Everything you need to<br /><span style={{ color: '#FF9A00' }}>sell digital products.</span>
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#F5E9CF', opacity: 0.8 }}>
              One platform. Zero compromise. From building pages to processing payments to delivering files — Funnely handles it all.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ FEATURE 1: AI Landing Page Builder ═══ */}
      <FeatureSection
        id="landing-pages" label="Landing Page Builder" dark={false}
        title="Click-to-edit sales pages that convert."
        desc="Choose from 5 premium templates or let AI write your entire page. Click any element to edit it directly. Switch color themes with one click. Add sections, reorder with floating toolbars, and preview on desktop, tablet, or phone — all in real time."
        bullets={['5 Premium Templates', 'Click-to-Edit', 'AI Copywriting', '1-Click Color Themes', 'Device Preview', 'Floating Toolbars']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-3 rounded-lg p-3" style={{ backgroundColor: 'white', border: '1px solid #19525915' }}>
                <div className="h-3 w-24 rounded bg-[#195259]/10 mx-auto mb-2" />
                <div className="h-5 w-44 rounded bg-[#195259]/20 mx-auto mb-1.5" />
                <div className="h-2 w-36 rounded bg-[#195259]/10 mx-auto mb-4" />
                <div className="grid grid-cols-3 gap-1.5 mb-3">
                  {[1,2,3].map(i => <div key={i} className="h-14 rounded-md" style={{ backgroundColor: '#F5E9CF', border: '1px solid #19525910' }}><div className="h-4 w-4 rounded mx-auto mt-3" style={{ backgroundColor: '#FF9A00', opacity: 0.4 }} /></div>)}
                </div>
                <div className="h-7 w-28 rounded-lg mx-auto" style={{ backgroundColor: '#FF9A00', opacity: 0.7 }} />
              </div>
              <div className="col-span-2 rounded-lg p-2.5" style={{ backgroundColor: 'white', border: '1px solid #19525915' }}>
                <div className="flex gap-1 mb-2">{['Edit', 'Products', 'Design', 'Settings'].map(t => <div key={t} className="flex-1 text-center py-1 rounded text-[5px] font-medium" style={{ backgroundColor: t === 'Edit' ? '#195259' : '#F5E9CF', color: t === 'Edit' ? '#F5E9CF' : '#195259' }}>{t}</div>)}</div>
                <div className="space-y-1.5">{[1,2,3,4,5].map(i => <div key={i} className="h-3.5 rounded" style={{ backgroundColor: '#F5E9CF' }} />)}</div>
              </div>
            </div>
            <div className="flex justify-center gap-1.5 mt-3">
              {[Monitor, Tablet, Smartphone].map((Icon, i) => <div key={i} className="p-1.5 rounded" style={{ backgroundColor: i === 0 ? '#195259' : '#19525915' }}><Icon className="h-3 w-3" style={{ color: i === 0 ? 'white' : '#195259' }} /></div>)}
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 2: AI Digital Studio ═══ */}
      <FeatureSection
        id="digital-studio" label="AI Digital Studio" dark={true} reversed={true}
        title="Create ebooks and digital products with AI."
        desc="Describe your idea and AI generates a complete multi-page ebook with professional layouts and stock images. Choose from 3 design directions — Light, Bold, or Dark. Export as PDF or DOCX. From idea to sellable product in minutes."
        bullets={['AI-Generated Content', 'Professional Layouts', 'PDF & DOCX Export', 'Stock Images Included']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#0F3338' }}>
            <p className="text-[8px] font-semibold uppercase tracking-wider mb-3" style={{ color: '#F5E9CF', opacity: 0.5 }}>AI-Generated Ebook</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[['Light', false], ['Bold', true], ['Dark', false]].map(([d, active]) => (
                <div key={d} className="rounded-lg p-2.5 text-center" style={{ backgroundColor: active ? '#195259' : 'rgba(255,255,255,0.06)', color: active ? '#F5E9CF' : 'rgba(255,255,255,0.5)', border: `1.5px solid ${active ? '#FF9A00' : 'rgba(255,255,255,0.1)'}` }}>
                  <div className="h-16 rounded mb-2" style={{ backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)' }}>
                    <div className="pt-3 px-2"><div className="h-1.5 w-10 rounded-full mx-auto mb-1" style={{ backgroundColor: active ? '#FF9A00' : 'rgba(255,255,255,0.15)', opacity: 0.6 }} /><div className="h-1 w-14 rounded-full mx-auto" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} /></div>
                  </div>
                  <p className="text-[7px] font-semibold">{d}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="flex-1 h-8 rounded-lg flex items-center justify-center text-[8px] font-semibold" style={{ backgroundColor: '#FF9A00', color: '#195259' }}>Export PDF</div>
              <div className="flex-1 h-8 rounded-lg flex items-center justify-center text-[8px] font-semibold" style={{ border: '1.5px solid rgba(255,255,255,0.2)', color: '#F5E9CF' }}>Export DOCX</div>
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 3: AI Growth Consultant ═══ */}
      <FeatureSection
        id="ai-consultant" label="AI Growth Consultant" dark={false}
        title="Your personal business advisor, 24/7."
        desc="Chat with an AI trained on marketing strategy, sales optimization, and creator growth tactics. Ask anything — from pricing strategy to content ideas to funnel optimization. Get real-time, actionable advice tailored to your business."
        bullets={['Real-Time Advice', 'Marketing Strategy', 'Pricing Optimization', 'Content Ideas']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="space-y-2.5">
              <div className="flex justify-end"><div className="rounded-xl rounded-br-sm px-3 py-2 text-[9px] max-w-[70%]" style={{ backgroundColor: '#195259', color: '#F5E9CF' }}>How should I price my ebook?</div></div>
              <div className="flex items-start gap-2">
                <div className="h-6 w-6 rounded-full shrink-0 flex items-center justify-center" style={{ backgroundColor: '#FF9A00' }}><Bot className="h-3 w-3 text-[#195259]" /></div>
                <div className="rounded-xl rounded-bl-sm px-3 py-2 text-[9px] leading-relaxed" style={{ backgroundColor: 'white', color: '#195259', border: '1px solid #19525915' }}>
                  Based on your niche, I'd recommend <strong>RM 49-79</strong> for a comprehensive ebook. Here's why:<br />
                  <span style={{ color: '#6D6E71' }}>1. Your target audience values premium content<br />2. Similar products in your niche range RM 39-99<br />3. Consider a launch price of RM 39 to build momentum</span>
                </div>
              </div>
              <div className="flex justify-end"><div className="rounded-xl rounded-br-sm px-3 py-2 text-[9px] max-w-[70%]" style={{ backgroundColor: '#195259', color: '#F5E9CF' }}>What about a bundle discount?</div></div>
              <div className="flex items-start gap-2">
                <div className="h-6 w-6 rounded-full shrink-0 flex items-center justify-center" style={{ backgroundColor: '#FF9A00' }}><Bot className="h-3 w-3 text-[#195259]" /></div>
                <div className="rounded-xl rounded-bl-sm px-3 py-2 text-[9px]" style={{ backgroundColor: 'white', color: '#195259', border: '1px solid #19525915' }}>Great idea! Bundle 3 products at <strong>20% off</strong> — it increases average order value by 40%.</div>
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 4: Multi-Gateway Checkout ═══ */}
      <FeatureSection
        id="checkout" label="Payment & Checkout" dark={true} reversed={true}
        title="Accept payments from anyone, anywhere."
        desc="Connect your preferred payment gateway and start accepting payments instantly. Support for FPX, credit cards, debit cards, and e-wallets. Customers checkout directly on your landing page — zero friction, zero redirects."
        bullets={['Billplz', 'ToyyibPay', 'SenangPay', 'Stripe', 'Midtrans', 'Xendit']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#0F3338' }}>
            <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-[9px] font-semibold text-white/80 mb-3">Checkout</p>
              <div className="space-y-2 mb-3">
                <div className="flex justify-between text-[8px]"><span style={{ color: '#F5E9CF' }}>Premium Template Pack</span><span style={{ color: '#FF9A00' }}>RM 79.00</span></div>
                <div className="flex justify-between text-[8px]"><span style={{ color: '#F5E9CF' }}>Design Bundle</span><span style={{ color: '#FF9A00' }}>RM 49.00</span></div>
                <div className="h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                <div className="flex justify-between text-[9px] font-bold"><span className="text-white">Total</span><span style={{ color: '#FF9A00' }}>RM 128.00</span></div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="h-7 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}><p className="text-[7px] text-white/40 text-center pt-2">Full Name</p></div>
                <div className="h-7 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}><p className="text-[7px] text-white/40 text-center pt-2">Email</p></div>
              </div>
              <div className="h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FF9A00' }}>
                <p className="text-[9px] font-semibold" style={{ color: '#195259' }}>Pay RM 128.00</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-3">
              {['Billplz', 'Stripe', 'FPX', 'Visa'].map(g => <span key={g} className="text-[7px] font-medium" style={{ color: 'rgba(255,255,255,0.3)' }}>{g}</span>)}
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 5: Sales Analytics ═══ */}
      <FeatureSection
        id="analytics" label="Sales Analytics" dark={false}
        title="Know exactly what's working."
        desc="Track revenue, orders, page views, unique visitors, and conversion rates — all in one dashboard. Filter by date range, export reports to Excel, and see which products and landing pages drive the most sales."
        bullets={['Revenue Tracking', 'Conversion Rates', 'Visitor Analytics', 'Export to Excel', 'Date Range Filters']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[['Total Revenue', 'RM 12,450', TrendingUp], ['Total Orders', '284', Package], ['Conversion', '4.2%', Target]].map(([l, v, Icon]) => (
                <div key={l} className="rounded-lg p-3 bg-white text-center" style={{ border: '1px solid #19525915' }}>
                  <Icon className="h-3.5 w-3.5 mx-auto mb-1" style={{ color: '#FF9A00' }} />
                  <p className="text-[7px] uppercase tracking-wider" style={{ color: '#6D6E71' }}>{l}</p>
                  <p className="text-sm font-bold" style={{ color: '#195259' }}>{v}</p>
                </div>
              ))}
            </div>
            <div className="rounded-lg p-3 bg-white" style={{ border: '1px solid #19525915' }}>
              <p className="text-[8px] font-semibold mb-2" style={{ color: '#195259' }}>Revenue Trend</p>
              <div className="flex items-end gap-1 h-20">
                {[30, 45, 35, 60, 50, 75, 65, 85, 70, 90, 80, 95].map((h, i) => (
                  <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, backgroundColor: i === 11 ? '#FF9A00' : '#195259', opacity: i === 11 ? 1 : 0.2 }} />
                ))}
              </div>
              <div className="flex justify-between mt-1">{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(m => <span key={m} className="text-[5px]" style={{ color: '#6D6E71' }}>{m}</span>)}</div>
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 6: Secure File Delivery ═══ */}
      <FeatureSection
        id="delivery" label="Automatic Delivery" dark={true} reversed={true}
        title="Secured and automatic file delivery."
        desc="Every purchase generates a unique, time-limited download link automatically. Set download limits and expiry windows. Customers get instant access after payment — no manual file sending, no WhatsApp, no email attachments."
        bullets={['Auto-Generated Links', 'Download Limits', 'Time-Limited Access', 'Instant After Payment']}
        mockup={
          <div className="p-5" style={{ backgroundColor: '#0F3338' }}>
            <div className="rounded-xl p-4 mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FF9A00' }}><ShieldCheck className="h-5 w-5 text-[#195259]" /></div>
                <div><p className="text-[9px] font-bold text-white">Secure Download Ready</p><p className="text-[7px] text-white/40">Auto-generated after payment</p></div>
              </div>
              <div className="rounded-lg p-2.5 mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-[7px] font-mono text-white/40 truncate">https://funnely.com/dl/a8f3k2m9x...</p>
              </div>
              <div className="flex gap-2">
                {[['5 downloads', Download], ['72h expiry', Clock], ['Auto-delivery', Zap]].map(([label, Icon]) => (
                  <div key={label} className="flex-1 rounded-lg p-2 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Icon className="h-3.5 w-3.5 mx-auto mb-1" style={{ color: '#FF9A00' }} />
                    <p className="text-[7px] font-medium text-white/60">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 7: Embeddable Checkout Forms ═══ */}
      <FeatureSection
        id="checkout-forms" label="Checkout Forms" dark={false}
        title="Sell from any website. 3 lines of code."
        desc="Generate an embeddable checkout form for any product. Copy the embed code and paste it on your blog, portfolio, or any website. Customers buy without leaving the page. Works with all payment gateways."
        bullets={['Embed Anywhere', 'No Redirects', 'All Gateways', 'Copy & Paste Setup']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="rounded-lg p-3 bg-white mb-3" style={{ border: '1px solid #19525915' }}>
              <p className="text-[8px] font-semibold mb-2" style={{ color: '#195259' }}>Embed Code</p>
              <div className="rounded-md p-2.5 font-mono text-[7px] leading-relaxed" style={{ backgroundColor: '#195259', color: '#FF9A00' }}>
                {'<script src="funnely.com/embed.js">'}<br />
                {'</script>'}<br />
                {'<div data-funnely="product_id">'}
              </div>
            </div>
            <div className="rounded-lg p-3 bg-white" style={{ border: '1px solid #19525915' }}>
              <p className="text-[8px] font-semibold mb-2" style={{ color: '#195259' }}>Preview on your website</p>
              <div className="rounded-md p-3" style={{ backgroundColor: '#F5E9CF', border: '1px solid #19525915' }}>
                <p className="text-[8px] font-bold mb-1" style={{ color: '#195259' }}>Premium Template Pack</p>
                <p className="text-[10px] font-bold mb-2" style={{ color: '#FF9A00' }}>RM 79.00</p>
                <div className="h-6 rounded flex items-center justify-center text-[7px] font-semibold text-white" style={{ backgroundColor: '#195259' }}>Buy Now</div>
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 8: Tracking Pixels ═══ */}
      <FeatureSection
        id="tracking" label="Built-in Tracking" dark={true} reversed={true}
        title="Facebook & TikTok pixel tracking built in."
        desc="Add your Facebook Pixel ID and TikTok Pixel ID directly in the landing page editor. Track page views, purchases, and conversions automatically. Optimize your ad campaigns with accurate attribution data — no code required."
        bullets={['Facebook Pixel', 'TikTok Pixel', 'Auto Page View Tracking', 'Purchase Events', 'Zero Code Setup']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#0F3338' }}>
            <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-[9px] font-semibold text-white/80 mb-3">Tracking Pixels</p>
              <div className="space-y-3">
                <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-5 w-5 rounded flex items-center justify-center" style={{ backgroundColor: '#1877F2' }}><Globe className="h-3 w-3 text-white" /></div>
                    <span className="text-[8px] font-semibold text-white">Facebook Pixel</span>
                  </div>
                  <div className="h-6 rounded-md flex items-center px-2 text-[7px] font-mono" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: '#FF9A00' }}>1234567890123456</div>
                </div>
                <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-5 w-5 rounded flex items-center justify-center bg-black"><Globe className="h-3 w-3 text-white" /></div>
                    <span className="text-[8px] font-semibold text-white">TikTok Pixel</span>
                  </div>
                  <div className="h-6 rounded-md flex items-center px-2 text-[7px] font-mono" style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: '#FF9A00' }}>CT1A2B3C4D5E6F7G8H</div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                {['PageView', 'Purchase', 'AddToCart'].map(e => <span key={e} className="text-[6px] px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(255,154,0,0.15)', color: '#FF9A00' }}>{e}</span>)}
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 9: Product Management ═══ */}
      <FeatureSection
        id="products" label="Product Management" dark={false}
        title="Sell anything digital. Courses, ebooks, templates, and more."
        desc="Upload any file type — PDFs, videos, ZIP archives, audio files. Create courses with structured modules. Bundle multiple products together at a discounted price. Set currencies (MYR, USD, IDR), download limits, and validity periods."
        bullets={['Any File Type', 'Courses & Modules', 'Product Bundles', 'Multi-Currency', 'Free & Paid Products']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="space-y-2">
              {[
                { name: 'Lightroom Preset Pack', type: 'Download', price: 'RM 49', badge: 'Published' },
                { name: 'Photography Masterclass', type: 'Course', price: 'RM 199', badge: 'Published' },
                { name: 'Design Template Bundle', type: 'Bundle', price: 'RM 79', badge: 'Draft' },
              ].map(p => (
                <div key={p.name} className="rounded-lg p-3 bg-white flex items-center gap-3" style={{ border: '1px solid #19525915' }}>
                  <div className="h-10 w-10 rounded-lg shrink-0 flex items-center justify-center" style={{ backgroundColor: '#195259' }}><Package className="h-4 w-4 text-white" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-bold truncate" style={{ color: '#195259' }}>{p.name}</p>
                    <p className="text-[7px]" style={{ color: '#6D6E71' }}>{p.type}</p>
                  </div>
                  <span className="text-[8px] font-bold shrink-0" style={{ color: '#FF9A00' }}>{p.price}</span>
                  <span className="text-[6px] px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: p.badge === 'Published' ? '#195259' : '#F5E9CF', color: p.badge === 'Published' ? '#F5E9CF' : '#195259' }}>{p.badge}</span>
                </div>
              ))}
            </div>
          </div>
        }
      />

      {/* ═══ FEATURE 10: Customer Management ═══ */}
      <FeatureSection
        id="customers" label="Customer Management" dark={true} reversed={true}
        title="Full control over every customer and order."
        desc="View every customer, their orders, download history, and payment status. Reissue download links, revoke access, manage validity periods. Export customer data anytime. Everything in one place — no spreadsheets needed."
        bullets={['Order History', 'Download Tracking', 'Reissue Links', 'Revoke Access', 'Export Data']}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#0F3338' }}>
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="p-3" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                <p className="text-[8px] font-semibold text-white/60 mb-2">Recent Customers</p>
              </div>
              {[
                { name: 'Sarah Ahmad', email: 'sarah@email.com', orders: 3, spent: 'RM 247' },
                { name: 'Amin Razak', email: 'amin@email.com', orders: 1, spent: 'RM 79' },
                { name: 'Lina Mohd', email: 'lina@email.com', orders: 5, spent: 'RM 495' },
              ].map((c, i) => (
                <div key={c.name} className="px-3 py-2.5 flex items-center gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="h-7 w-7 rounded-full shrink-0 flex items-center justify-center text-[8px] font-bold" style={{ backgroundColor: '#FF9A00', color: '#195259' }}>{c.name[0]}</div>
                  <div className="flex-1 min-w-0"><p className="text-[8px] font-semibold text-white truncate">{c.name}</p><p className="text-[6px] text-white/40">{c.email}</p></div>
                  <div className="text-right shrink-0"><p className="text-[8px] font-bold" style={{ color: '#FF9A00' }}>{c.spent}</p><p className="text-[6px] text-white/30">{c.orders} orders</p></div>
                </div>
              ))}
            </div>
          </div>
        }
      />

      {/* ═══ CTA ═══ */}
      <section className="py-24 sm:py-32 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <img src="/images/marketing/cta-mascot.png" alt="" loading="lazy" className="w-[200px] sm:w-[260px] h-auto mx-auto mb-8 object-contain" style={{ animation: 'float 4s ease-in-out infinite' }} />
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-5">Ready to start selling?</h2>
            <p className="text-lg mb-10" style={{ color: '#F5E9CF', opacity: 0.8 }}>Your digital store is 5 minutes away.</p>
            <Button size="lg" className="text-base px-12 h-14 font-semibold text-[#195259] rounded-full shadow-lg hover:shadow-xl transition-shadow" style={{ backgroundColor: '#FF9A00' }} onClick={() => navigate('/register')}>
              Create Your Store Now <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Reveal>
        </div>
      </section>

      <style>{`@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }`}</style>

      {/* Footer */}
      <footer className="py-10 px-4" style={{ backgroundColor: '#0F3338' }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div><img src="/logos/funnely-white.png" alt="Funnely" className="h-5 object-contain" /><p className="text-xs mt-1" style={{ color: '#6D6E71' }}>The easiest way to sell digital products.</p></div>
          <div className="flex items-center gap-6 text-sm" style={{ color: '#6D6E71' }}>
            <a href="/features" className="hover:text-white transition-colors">Features</a>
            <a href="/#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="/login" className="hover:text-white transition-colors">Log In</a>
          </div>
          <p className="text-xs" style={{ color: '#6D6E71' }}>&copy; 2026 Funnely. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
